import { memo, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';

import { auth, firestore } from '@tabletuck/firebase-services';
import { setUser, UpdateCartData, UdpateNote } from '@tabletuck/redux-slices';
import { useAnonymousAuth } from '../../hooks/useAnonymousAuth';
import { useDeliveryAddress } from '../../hooks/useDeliveryAddress';
import { useCartPricing } from '../../hooks/useCartPricing';

import CartHeader from './components/CartHeader';
import OrderTypeToggle from './components/OrderTypeToggle';
import AddressSection from './components/AddressSection';
import CartItemsList from './components/CartItemsList';
import CartSummary from './components/CartSummary';
import ActionButtons from './components/ActionButtons';
import DeliveryDialog from '../CartDrawer/DeliveryDialog';
import { environment } from 'apps/table-tuck-web/src/environments/environment';
import { calculateDistance } from '../../services/locationService';

/**
 * High-performance Cart Drawer for Fatty Patty
 * Optimized with memoization and component separation
 */
const FattyCartDrawer = memo(
  ({ handleCloseCartDialoge, openCartDialoge, userData }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Refs
    const hasSyncedCartRef = useRef(false);
    const leaveTimerRef = useRef(null);

    // Anonymous auth
    const { isAnonymous, syncCartFromFirebase } = useAnonymousAuth();

    // Delivery address management
    const {
      currentDeliveryAddressId,
      currentDeliveryAddressData,
      setDeliveryAddressById,
      refreshAddresses,
    } = useDeliveryAddress();

    // Redux selectors (memoized)
    const cart = useSelector((state) => state.shopReducer.cart);
    const stores = useSelector((state) => state.shopReducer.stores);
    const addresses = useSelector((state) => state.authReducer.userAddresses);
    const cartData = useSelector((state) => state.shopReducer.cartData);
    const { scheduledTime: cartDataScheduledTime } = cartData;
    const reduxScheduledTime = useSelector(
      (state) => state.shopReducer.scheduledTime
    );
    const note = useSelector((state) => state.shopReducer.note);
    const orderTypeLocked = useSelector((state) => state.shopReducer.orderTypeLocked);

    // Local note state avoids Redux dispatch + full re-render on every keystroke.
    // This is important for iOS/WebKit stability inside drawers/modals.
    const [localNote, setLocalNote] = useState(note || '');
    const noteDebounceTimeoutRef = useRef(null);
    const noteTextareaRef = useRef(null);
    const wasOpenRef = useRef(false);

    // Measure visible header height so the drawer starts below the navbar only when it's visible.
    // Uses getBoundingClientRect so if user has scrolled down and the header is out of view,
    // the drawer takes the full height instead of leaving a gap at the top.
    const [headerHeight, setHeaderHeight] = useState(0);
    useEffect(() => {
      if (openCartDialoge) {
        const header = document.querySelector('header');
        if (header) {
          const rect = header.getBoundingClientRect();
          // rect.bottom is the distance from viewport top to the bottom of the header.
          // If it's <= 0 the header has scrolled out of view — use 0 (full-height drawer).
          setHeaderHeight(rect.bottom > 0 ? Math.round(rect.bottom) : 0);
        }
      }
    }, [openCartDialoge]);

    // Local state
    const [orderType, setOrderType] = useState(
      () => userData?.pickupOrDelivery || 'pickup'
    );
    const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
    const [forceDeliveryDialog, setForceDeliveryDialog] = useState(false);
    const [deliveryTooFarDialog, setDeliveryTooFarDialog] = useState(false);

    // Local state for DeliveryDialog compatibility
    // These are used by DeliveryDialog but don't affect FattyCartDrawer's display
    const [selectedAddressDisplay, setSelectedAddressDisplay] = useState('');
    const [addressStoreId, setAddressStoreId] = useState('');
    const [deliveryError, setDeliveryError] = useState('');

    // Derive cart store ID
    const cartStoreId = useMemo(() => {
      if (Array.isArray(cart) && cart.length > 0) {
        return cart[0]?.storeId ?? cart[0]?.store_id ?? '';
      }
      return '';
    }, [cart]);

    // Get current store data
    const currentStore = useMemo(
      () => stores?.find((s) => s.id === cartStoreId),
      [stores, cartStoreId]
    );

    // Cart pricing hook
    const {
      apiPricingData,
      isCalculatingPrice,
      cartLoading,
      pricingError,
      callCalculateSquareItemsPriceImmediate,
    } = useCartPricing({
      cart,
      cartStoreId,
      orderType,
      deliveryAddressId: currentDeliveryAddressId,
      enabled: openCartDialoge && !!auth?.currentUser?.uid,
    });

    // Helper to format store address into a US-style single-line string
    const formatAddress = useCallback((address) => {
      if (!address) return '';
      const line1 =
        address.addressLine1 || address.address_line1 || address.address1 || '';
      const line2 =
        address.addressLine2 || address.address_line2 || address.address2 || '';
      const city = address.locality || address.city || '';
      const state = address.administrativeDistrictLevel1 || address.state || '';
      const postal =
        address.postalCode ||
        address.zipCode ||
        address.postal_code ||
        address.zip ||
        '';

      const parts = [];
      if (line1) parts.push(line1);
      if (line2) parts.push(line2);

      // Combine city, state and postal into one part
      const cityState = [city, state].filter(Boolean).join(', ');
      const cityStatePostal = [cityState, postal].filter(Boolean).join(' ');
      if (cityStatePostal) parts.push(cityStatePostal);

      return parts.join(', ');
    }, []);

    // Memoized display values for better performance
    const displaySubtotal = useMemo(() => {
      return apiPricingData?.totalPrice?.amount
        ? parseFloat(apiPricingData.totalPrice.amount) / 100
        : 0;
    }, [apiPricingData]);

    const displayTotal = useMemo(() => {
      return apiPricingData?.totalPrice?.amount
        ? parseFloat(apiPricingData.totalPrice.amount) / 100
        : 0;
    }, [apiPricingData]);

    const displayTax = useMemo(() => {
      return apiPricingData?.totalTax?.amount
        ? parseFloat(apiPricingData.totalTax.amount) / 100
        : 0;
    }, [apiPricingData]);

    const displayModifiersPrice = useMemo(() => {
      return apiPricingData?.totalModifiersPrice?.amount
        ? parseFloat(apiPricingData.totalModifiersPrice.amount) / 100
        : 0;
    }, [apiPricingData]);

    const displayTotalDiscounts = useMemo(() => {
      return apiPricingData?.totalDiscounts?.amount
        ? parseFloat(apiPricingData.totalDiscounts.amount) / 100
        : 0;
    }, [apiPricingData]);

    const displayServiceCharge = useMemo(() => {
      return apiPricingData?.totalServiceCharge?.amount
        ? parseFloat(apiPricingData.totalServiceCharge.amount) / 100
        : apiPricingData?.deliveryFee?.amount
        ? parseFloat(apiPricingData.deliveryFee.amount) / 100
        : 0;
    }, [apiPricingData]);

    const displayOnlineFee = useMemo(() => {
      return apiPricingData?.onlineFee?.amount
        ? parseFloat(apiPricingData.onlineFee.amount) / 100
        : 0;
    }, [apiPricingData]);

    // Sync cart and refresh addresses on drawer open
    // Note: We don't manually call pricing API here - the useCartPricing hook
    // automatically watches cart changes and calls the API when needed
    useEffect(() => {
      if (!openCartDialoge) {
        hasSyncedCartRef.current = false;
        return;
      }

      if (
        openCartDialoge &&
        syncCartFromFirebase &&
        !hasSyncedCartRef.current
      ) {
        hasSyncedCartRef.current = true;

        if (auth?.currentUser?.uid && cart && cart.length > 0) {
          callCalculateSquareItemsPriceImmediate().catch(() => {
            // Silently catch errors - pricing will retry when cart syncs
          });
        }

        // Sync cart from Firebase in background (non-blocking)(Commented no need)
        // If sync brings different data, useCartPricing hook will auto-update pricing
        // syncCartFromFirebase()
        //   .then((result) => {
        //     // If sync brings new cart data, pricing will auto-update via useCartPricing hook
        //     // which watches cart changes
        //     if (result.success) {
        //       // Cart synced successfully - pricing will update automatically
        //     }
        //   })
        //   .catch(() => {
        //     // Silently catch errors during cart sync
        // });

        // Also refresh addresses to ensure they're up to date
        if (auth?.currentUser?.uid) {
          refreshAddresses().catch(() => {
            // Silently catch errors during address refresh
          });
        }
      }
    }, [openCartDialoge, syncCartFromFirebase, refreshAddresses]);

    // Sync order type with user preference only on mount or when userData changes
    // Don't include orderType in deps to avoid conflicts with user interactions
    useEffect(() => {
      if (userData?.pickupOrDelivery) {
        setOrderType(userData.pickupOrDelivery);
      }
    }, [userData?.pickupOrDelivery]);

    // Clear note when cart becomes empty
    useEffect(() => {
      if (cart && cart.length === 0 && note) {
        if (noteDebounceTimeoutRef.current) {
          clearTimeout(noteDebounceTimeoutRef.current);
          noteDebounceTimeoutRef.current = null;
        }
        setLocalNote('');
        dispatch(UdpateNote(''));
      }
    }, [cart, note]);

    // When the drawer opens, sync local note from Redux.
    useEffect(() => {
      if (openCartDialoge) {
        setLocalNote(note || '');
      }
    }, [openCartDialoge, note]);

    const flushNoteDispatch = useCallback(
      (nextValue) => {
        const valueToDispatch =
          nextValue !== undefined ? nextValue : localNote;

        // Avoid dispatching if Redux already has the same value.
        if (valueToDispatch === note) return;

        if (noteDebounceTimeoutRef.current) {
          clearTimeout(noteDebounceTimeoutRef.current);
          noteDebounceTimeoutRef.current = null;
        }

        dispatch(UdpateNote(valueToDispatch));
      },
      [dispatch, localNote, note]
    );

    const handleCloseCartDialogeSafe = useCallback(() => {
      flushNoteDispatch();
      handleCloseCartDialoge();
    }, [flushNoteDispatch, handleCloseCartDialoge]);

    const handleDrawerMouseLeave = useCallback(() => {
      leaveTimerRef.current = setTimeout(() => {
        handleCloseCartDialogeSafe();
      }, 400);
    }, [handleCloseCartDialogeSafe]);

    const handleDrawerMouseEnter = useCallback(() => {
      if (leaveTimerRef.current) {
        clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
    }, []);

    // If drawer closes while the user is typing, make sure we don't lose the latest note.
    useEffect(() => {
      if (wasOpenRef.current && !openCartDialoge) {
        flushNoteDispatch();
      }
      wasOpenRef.current = openCartDialoge;
    }, [openCartDialoge, flushNoteDispatch]);

    // Cleanup pending timers on unmount.
    useEffect(() => {
      return () => {
        if (noteDebounceTimeoutRef.current) {
          clearTimeout(noteDebounceTimeoutRef.current);
          noteDebounceTimeoutRef.current = null;
        }
        if (leaveTimerRef.current) {
          clearTimeout(leaveTimerRef.current);
          leaveTimerRef.current = null;
        }
      };
    }, []);

    // Handle order type change
    const handleOrderTypeChange = useCallback(
      (newType) => {
        if (newType === orderType) return;

        // Check if delivery address is required
        if (newType === 'delivery') {
          const hasAddress =
            currentDeliveryAddressData || (addresses && addresses.length > 0);
          if (!hasAddress) {
            setForceDeliveryDialog(true);
            setDeliveryDialogOpen(true);
            return;
          }

          // Check 5-mile delivery radius restriction
          if (currentDeliveryAddressData?.lat && currentDeliveryAddressData?.lng) {
            const storeCoords =
              currentStore?._geoloc ||
              currentStore?.coordinates ||
              currentStore?.address;
            if (storeCoords?.lat && storeCoords?.lng) {
              const distanceKm = calculateDistance(storeCoords, currentDeliveryAddressData);
              if (distanceKm !== null && distanceKm * 0.621371 > 5) {
                setDeliveryTooFarDialog(true);
                return;
              }
            }
          }
        }

        // Update local state immediately
        setOrderType(newType);

        // Update Redux with new preference
        const updatedUser = { ...userData, pickupOrDelivery: newType };
        dispatch(setUser({ userData: updatedUser }));

        // Persist to Firestore in background
        if (auth?.currentUser?.uid) {
          firestore
            .collection('users')
            .doc(auth.currentUser.uid)
            .set({ pickupOrDelivery: newType }, { merge: true })
            .catch(() => {
              // Silently catch Firestore errors
            });
        }

        // Recalculate pricing with new order type
        const deliveryId =
          newType === 'delivery' ? currentDeliveryAddressId || '' : '';
        callCalculateSquareItemsPriceImmediate(newType, deliveryId);
      },
      [
        orderType,
        currentDeliveryAddressData,
        addresses,
        currentDeliveryAddressId,
        userData,
        dispatch,
        callCalculateSquareItemsPriceImmediate,
        currentStore,
      ]
    );

    // Handle new address added (matches Cart.jsx implementation)
    // Uses source: 'deliveryDialog' which has lower priority than 'branchMenu'
    const handleNewAddressAdded = useCallback(
      async (addressId) => {
        try {
          // Refresh addresses to get the newly added one
          await refreshAddresses();

          // Use the unified hook to set this as the delivery address
          // source: 'deliveryDialog' - lower priority than branchMenu
          const storeId = cartStoreId || '';
          await setDeliveryAddressById(addressId, storeId, 'deliveryDialog');

          // Find the newly added address for additional updates
          const addressesRef = firestore
            .collection('users')
            .doc(auth?.currentUser?.uid)
            .collection('addresses');
          const addressDoc = await addressesRef.doc(addressId).get();

          if (addressDoc.exists) {
            const newAddress = { id: addressDoc.id, ...addressDoc.data() };
            console.log('Selected new delivery address:', newAddress);

            // Update user preference in Redux
            const updatedUser = {
              ...userData,
              defaultDeliveryAddress: addressId,
              pickupOrDelivery: 'delivery',
            };
            dispatch(setUser({ userData: updatedUser }));

            // Persist to Firestore
            if (auth?.currentUser?.uid) {
              await firestore
                .collection('users')
                .doc(auth.currentUser.uid)
                .update({
                  defaultDeliveryAddress: addressId,
                  pickupOrDelivery: 'delivery',
                });
            }
          }

          // Ensure delivery type is selected
          setOrderType('delivery');

          // Clear force delivery dialog flag and close the dialog
          setForceDeliveryDialog(false);
          setDeliveryDialogOpen(false);

          // Recalculate pricing with delivery type and new address
          await callCalculateSquareItemsPriceImmediate('delivery', addressId);
        } catch (error) {
          console.error('Error adding new address:', error);
        }
      },
      [
        refreshAddresses,
        setDeliveryAddressById,
        cartStoreId,
        callCalculateSquareItemsPriceImmediate,
        userData,
        dispatch,
      ]
    );

    // Handle address change
    const handleAddressChange = useCallback(
      async (addressId) => {
        try {
          const selectedAddress = addresses?.find((a) => a.id === addressId);
          if (selectedAddress) {
            const storeId = selectedAddress?.delivery_store_id || cartStoreId;
            await setDeliveryAddressById(addressId, storeId, 'deliveryDialog');
            setDeliveryDialogOpen(false);

            // Switch to delivery tab
            setOrderType('delivery');
            const updatedUser = {
              ...userData,
              defaultDeliveryAddress: addressId,
              pickupOrDelivery: 'delivery',
            };
            dispatch(setUser({ userData: updatedUser }));
            if (auth?.currentUser?.uid) {
              firestore
                .collection('users')
                .doc(auth.currentUser.uid)
                .set({ pickupOrDelivery: 'delivery' }, { merge: true })
                .catch(() => {});
            }

            await callCalculateSquareItemsPriceImmediate('delivery', addressId);
          }
        } catch (error) {
          // Error changing address
        }
      },
      [
        addresses,
        cartStoreId,
        setDeliveryAddressById,
        callCalculateSquareItemsPriceImmediate,
        userData,
        dispatch,
      ]
    );

    // Navigate back to menu
    const handleBackToMenu = useCallback(() => {
      console.log('\n🔙🔙🔙 [FattyCartDrawer] === NAVIGATING BACK TO MENU ===');
      console.log('🔙 [FattyCartDrawer] Cart Items:', cart);
      console.log('🔙 [FattyCartDrawer] Cart Items Count:', cart?.length || 0);
      console.log('🔙 [FattyCartDrawer] Order Type:', orderType);
      console.log('🔙 [FattyCartDrawer] Cart Store ID:', cartStoreId);
      console.log('🔙 [FattyCartDrawer] Current Store:', currentStore);
      console.log(
        "🔙 [FattyCartDrawer] Cart's Original Scheduled Time (from cartData):",
        cartDataScheduledTime
      );
      console.log(
        '🔙 [FattyCartDrawer] Scheduled Time Store ID:',
        cartDataScheduledTime?.storeId
      );
      console.log('🔙 [FattyCartDrawer] Navigation State:', {
        store: currentStore || null,
        preserveOrderType: orderType,
        preserveScheduledTime: cartDataScheduledTime || null,
        fromCart: true,
        storeId: cartStoreId,
      });
      console.log('🔙🔙🔙 [FattyCartDrawer] === END NAVIGATION DATA ===\n');

      handleCloseCartDialogeSafe();
      navigate('/menu', {
        state: {
          store: currentStore || null,
          preserveOrderType: orderType,
          preserveScheduledTime: cartDataScheduledTime || null,
          fromCart: true,
          storeId: cartStoreId,
        },
      });
    }, [
      currentStore,
      orderType,
      cartDataScheduledTime,
      cartStoreId,
      cart,
      handleCloseCartDialogeSafe,
      navigate,
    ]);

    // Navigate to cart page
    const handleNavigateToCart = useCallback(() => {
      // Determine final scheduled time:
      // 1. If cartData already has scheduledTime for this cart's store, keep it
      // 2. Else if Redux scheduledTime matches cart's store, use it
      // 3. Otherwise, clear it (null)
      let finalScheduledTime = null;

      if (
        cartDataScheduledTime &&
        cartDataScheduledTime.storeId === cartStoreId
      ) {
        // Keep existing cart's scheduled time
        finalScheduledTime = cartDataScheduledTime;
      } else if (
        reduxScheduledTime &&
        reduxScheduledTime.storeId === cartStoreId
      ) {
        // Use Redux scheduled time if it matches cart store
        finalScheduledTime = reduxScheduledTime;
      }

      // Update Redux cart data before navigation (same as handleNavigateToCheckout)
      const defaultAdd =
        orderType === 'delivery' && currentDeliveryAddressData
          ? currentDeliveryAddressData.displayName ||
            currentDeliveryAddressData.address ||
            formatAddress(currentDeliveryAddressData)
          : '';
      const storeAddressDetails = currentStore
        ? formatAddress(currentStore)
        : '';
      const typeAddress =
        orderType === 'delivery' ? defaultAdd : storeAddressDetails;

      dispatch(
        UpdateCartData({
          subTotal: displaySubtotal,
          typeAddress,
          orderType: orderType,
          storeId: cartStoreId || '',
          apiPricingData: apiPricingData,
          backendTotal: displayTotal,
          backendTax: displayTax,
          backendSubtotal: displaySubtotal,
          backendTotalDiscounts: displayTotalDiscounts,
          backendDeliveryFee: displayServiceCharge,
          scheduledTime: finalScheduledTime,
        })
      );

      if (typeof document !== 'undefined') {
        const active = document.activeElement;
        if (active && typeof active.blur === 'function') {
          active.blur();
        }
      }

      handleCloseCartDialogeSafe();
      setTimeout(() => {
        console.log('Navigating to /cart with deliveryId:', currentDeliveryAddressId || '');
        navigate('/cart', {
          state: {
            apiPricingData: apiPricingData,
            storeId: cartStoreId || '',
            defaultAdd: defaultAdd || '',
            fromAnonymous: isAnonymous,
            deliveryId: currentDeliveryAddressId || '',
            storeAddressDetails: storeAddressDetails,
          },
        });
      }, 0);
    }, [
      apiPricingData,
      cartStoreId,
      currentDeliveryAddressId,
      currentDeliveryAddressData,
      currentStore,
      orderType,
      cartDataScheduledTime,
      reduxScheduledTime,
      displaySubtotal,
      displayTotal,
      displayTax,
      displayTotalDiscounts,
      displayServiceCharge,
      isAnonymous,
      dispatch,
      formatAddress,
      handleCloseCartDialogeSafe,
      navigate,
    ]);

   

    // Detect iOS (Safari/WebView) to apply safer behavior for text inputs in Drawer
    const isIOS =
      typeof navigator !== 'undefined' &&
      /iPad|iPhone|iPod/.test(navigator.userAgent);

    const handleNoteFocus = useCallback(() => {
      if (!isIOS) return;
      const el = noteTextareaRef.current;
      if (!el || typeof el.scrollIntoView !== 'function') return;

      // iOS may try to auto-scroll focused inputs. Keep it "nearest" to
      // avoid large scroll jumps inside the drawer's scrolling container.
      try {
        requestAnimationFrame(() => {
          try {
            el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          } catch {
            // Ignore scroll exceptions
          }
        });
      } catch {
        // Ignore RAF exceptions
      }
    }, [isIOS]);

    // Cache for RTL support (LTR only for English)
    const cacheRtl = useMemo(
      () =>
        createCache({
          key: 'muirtl',
          stylisPlugins: [prefixer],
        }),
      []
    );

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={openCartDialoge}
      onClose={handleCloseCartDialogeSafe}
      sx={{
        zIndex: 1400,
        top: `${headerHeight}px`,
        '& .MuiBackdrop-root': { top: `${headerHeight}px` },
      }}
      ModalProps={{
        keepMounted: false,
        disableEnforceFocus: true,
        disableRestoreFocus: true,
      }}
      PaperProps={{
        onMouseEnter: handleDrawerMouseEnter,
        onMouseLeave: handleDrawerMouseLeave,
        sx: {
          width: { xs: '100%', lg: '500px' },
          maxWidth: '100%',
          top: `${headerHeight}px`,
          height: `calc(100% - ${headerHeight}px)`,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        },
      }}
    >
      {/* Only render content when drawer is actually open to force clean state */}
      {openCartDialoge && (
        <>
          <DeliveryDialog
            open={deliveryDialogOpen}
            handleClose={() => {
              setDeliveryDialogOpen(false);
              setForceDeliveryDialog(false);
            }}
            token={userData?.uid}
            items={addresses}
            handleDefault={setSelectedAddressDisplay}
            setErr={setDeliveryError}
            setApiError={setDeliveryError}
            handleFee={() => {}}
            setAddressStore={setAddressStoreId}
            currentStore={currentStore}
            onAddressChange={
              forceDeliveryDialog ? handleNewAddressAdded : handleAddressChange
            }
          />

          <Dialog
            open={deliveryTooFarDialog}
            onClose={() => setDeliveryTooFarDialog(false)}
            sx={{ zIndex: 1500 }}
          >
            <DialogTitle sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700 }}>
              Delivery Not Available
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ fontFamily: 'Jost, sans-serif', color: '#172B4D' }}>
                Delivery is not available for this address. Your delivery
                address is more than 5 miles from the store. If you want to
                continue, please choose another address.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ padding: '16px', gap: 1 }}>
              <Button
                onClick={() => {
                  setDeliveryTooFarDialog(false);
                  setForceDeliveryDialog(false);
                  setDeliveryDialogOpen(true);
                }}
                variant="contained"
                disableRipple
                sx={{
                  backgroundColor: environment.mainColor,
                  color: 'white',
                  borderRadius: '43px',
                  fontFamily: 'Jost',
                  textTransform: 'capitalize',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: environment.mainColor, boxShadow: 'none' },
                }}
              >
                Choose Another Address
              </Button>
              <Button
                onClick={() => setDeliveryTooFarDialog(false)}
                variant="outlined"
                disableRipple
                sx={{
                  borderRadius: '43px',
                  fontFamily: 'Jost',
                  textTransform: 'capitalize',
                  borderColor: '#E6E6E6',
                  color: '#172B4D',
                  '&:hover': { borderColor: '#E6E6E6', backgroundColor: '#f5f5f5' },
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          <Box
            dir="ltr"
            onClick={(e) => e.stopPropagation()}
            sx={{
              padding: '10px',
              // Remove nested scroll - let drawer paper handle it
              cursor: 'default',
            }}
          >
          <CartHeader
            itemCount={apiPricingData?.lineItemData?.length || 0}
                onClose={handleCloseCartDialogeSafe}
          />

          {auth?.currentUser &&
            cart?.length > 0 &&
            !(pricingError && apiPricingData === null) && (
              <>
                {/* <OrderTypeToggle
                  orderType={orderType}
                  onOrderTypeChange={handleOrderTypeChange}
                  enablePickup={currentStore?.pickup_enabled}
                  enableDelivery={currentStore?.delivery_enabled}
                  locked={!!orderTypeLocked}
                /> */}

                <AddressSection
                  orderType={orderType}
                  currentStore={currentStore}
                  currentAddress={currentDeliveryAddressData}
                  onChangeAddress={() => setDeliveryDialogOpen(true)}
                />
              </>
            )}

          <CacheProvider value={cacheRtl}>
            <CartItemsList
              apiPricingData={apiPricingData}
              cartLoading={cartLoading}
              pricingError={pricingError}
              orderType={orderType}
              onAddItems={handleBackToMenu}
              cart={cart}
            />
          </CacheProvider>

          {apiPricingData?.lineItemData?.length > 0 && (
            // Notes TextField intentionally rendered outside CacheProvider for
            // an A/B test: Emotion style injection on focus can be problematic on iOS.
            <Box sx={{ width: '100%', padding: '12px 5%' }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Special Instructions"
                value={localNote || ''}
                inputRef={noteTextareaRef}
                inputProps={{
                  autoComplete: 'off',
                  autoCorrect: 'off',
                  autoCapitalize: 'off',
                  spellCheck: 'false',
                  inputMode: 'text',
                }}
                onFocus={handleNoteFocus}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setLocalNote(nextValue);

                  // Debounce Redux updates: avoids massive re-render frequency on iOS.
                  if (noteDebounceTimeoutRef.current) {
                    clearTimeout(noteDebounceTimeoutRef.current);
                  }

                  noteDebounceTimeoutRef.current = setTimeout(() => {
                    dispatch(UdpateNote(nextValue));
                    noteDebounceTimeoutRef.current = null;
                  }, 400);
                }}
                onBlur={() => flushNoteDispatch()}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '16px',
                    '& fieldset': {
                      borderColor: '#E6E6E6',
                    },
                    '&:hover fieldset': {
                      borderColor: '#E6E6E6',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: environment.mainColor,
                    },
                  },
                  '& .MuiInputBase-input': {
                    WebkitAppearance: 'none',
                    '&::placeholder': {
                      fontFamily: 'Jost, sans-serif',
                      color: '#B0B0B0',
                      fontSize: '16px',
                      opacity: 1,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: 'Jost, sans-serif',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: environment.mainColor,
                  },
                }}
              />
            </Box>
          )}

          {apiPricingData?.lineItemData?.length > 0 && (
            <CacheProvider value={cacheRtl}>
              <CartSummary
                apiPricingData={apiPricingData}
                orderType={orderType}
                storeData={currentStore}
              />

              <ActionButtons
                onAddMoreItems={handleBackToMenu}
                onGoToCart={handleNavigateToCart}
                hasErrors={false}
              />
            </CacheProvider>
          )}
          </Box>
        </>
      )}
    </Drawer>
  );
}
);

FattyCartDrawer.displayName = 'FattyCartDrawer';

export default FattyCartDrawer;
